import { Card, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import SWR_KEYS from "@/constants/swr-keys";
import useCreatePaymentType from "@/hooks/requests/t2Pay/use-create-payment-type";
import useGetAvailablePaymentTypes from "@/hooks/requests/t2Pay/use-get-available-payment-types";
import useGetPaymentTypes from "@/hooks/requests/t2Pay/use-get-payment-types";
import useRemovePaymentType from "@/hooks/requests/t2Pay/use-remove-payment-type";
import useUpdatePaymentType from "@/hooks/requests/t2Pay/use-update-payment-type";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import {
  PaymentType,
  PaymentTypeUpdateInput,
} from "@/interfaces/payment-type.interface";
import {
  PAYMENT_TYPE_MESSAGES,
  PaymentTypeMessages,
} from "@/messages/platforms/talk2PayHome/payment-type.messages";

import { PaymentTypeDialog } from "./components/paymentTypeDialog";
import { PaymentTypeTable } from "./components/paymentTypeTable";
import NoPaymentTypeMessage from "./components/noPaymentType";
import { PaymentTypeFormData } from "./form-data";

type Props = {
  messages?: PaymentTypeMessages;
};

function PaymentTypeCard({ messages = PAYMENT_TYPE_MESSAGES }: Props) {
  const { isVisible, selected, handleOpen, handleClose } = useDialog<PaymentType>();
  const { data, isLoading: isLoadingList, error } = useGetPaymentTypes();
  const { data: available } = useGetAvailablePaymentTypes();
  const { create, isLoading: isLoadingCreate } = useCreatePaymentType();
  const { update, isLoading: isLoadingUpdate } = useUpdatePaymentType();
  const { remove, isLoading: isLoadingRemove } = useRemovePaymentType();
  const { mutate } = useMutate();

  const items = data ?? [];
  const hasItems = items.length > 0;
  const allTypesAdded = Array.isArray(available) && available.length === 0;

  const refresh = () => {
    mutate(SWR_KEYS.getPaymentTypes);
    mutate(SWR_KEYS.getAvailablePaymentTypes);
  };

  const onSuccess = () => {
    refresh();
    handleClose();
  };

  const onSubmit = async (formData: PaymentTypeFormData) => {
    if (selected) {
      const updatePayload: PaymentTypeUpdateInput = {
        active: formData.active,
        discountApplied: Number(formData.discountApplied) || 0,
        discountOver: Number(formData.discountOver) || 0,
        maxInstallmentCount: Number(formData.maxInstallmentCount) || 1,
        minInstallmentAmount: Number(formData.minInstallmentAmount) || 0,
        monthlyInterestRates: formData.monthlyInterestRates || "{}",
      };
      await update({
        _id: selected.id,
        formData: updatePayload,
        successMessage: messages.updateSuccess,
        errorMessage: messages.updateError,
        onSuccess,
      });
    } else {
      await create({
        formData: {
          paymentTypesID: Number(formData.paymentTypesID),
          active: true,
          discountApplied: Number(formData.discountApplied) || 0,
          discountOver: Number(formData.discountOver) || 0,
          maxInstallmentCount: Number(formData.maxInstallmentCount) || 1,
          minInstallmentAmount: Number(formData.minInstallmentAmount) || 0,
          monthlyInterestRates: formData.monthlyInterestRates || "{}",
        },
        successMessage: messages.createSuccess,
        errorMessage: messages.createError,
        onSuccess,
      });
    }
  };

  const onRemove = async (item: PaymentType) => {
    await remove({
      _id: item.id,
      successMessage: messages.deleteSuccess,
      errorMessage: messages.deleteError,
      onSuccess: refresh,
    });
  };

  const isMutating = isLoadingCreate || isLoadingUpdate || isLoadingRemove;
  const isLoading = isLoadingList || isMutating;

  return (
    <Card>
      <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h5">{messages.cardTitle}</MDTypography>
      </MDBox>

      <MDBox pb={3} px={3}>
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {isLoadingList && <Skeleton variant="rectangular" width="100%" height={80} />}

          {!isLoadingList && error && (
            <AlertBlock
              title={messages.errorTitle}
              description={messages.errorDescription}
              alt={messages.errorAlt}
            />
          )}

          {!isLoadingList && !error && !hasItems && (
            <NoPaymentTypeMessage messages={messages} handleOpen={() => handleOpen()} />
          )}

          {!isLoadingList && !error && hasItems && (
            <PaymentTypeTable
              items={items}
              messages={messages}
              handleOpen={() => handleOpen()}
              onEdit={(item) => handleOpen(item)}
              onRemove={onRemove}
              disableAdd={allTypesAdded}
            />
          )}

          <PaymentTypeDialog
            isOpen={isVisible}
            onClose={handleClose}
            messages={messages}
            isLoading={isMutating}
            selected={selected}
            onSubmit={onSubmit}
          />
        </Grid>
      </MDBox>
    </Card>
  );
}

export default PaymentTypeCard;
